/**
 * Shopify Quantity Gifts Manager
 * A clean, robust system for managing quantity-based gift functionality
 * 
 * Replaces the fragmented gift logic previously spread across:
 * - layout/theme.liquid (500+ lines)
 * - sections/main-product.liquid 
 * - snippets/quantity-gifts.liquid
 * - assets/global1.js
 */

class CartAPI {
  /**
   * Utility class for Shopify cart API operations
   */
  static async getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('CartAPI.getCart error:', error);
      throw error;
    }
  }

  static async addItem(variantId, quantity) {
    try {
      // Input validation
      if (!variantId) throw new Error('Variant ID is required');
      if (quantity < 0) throw new Error('Quantity cannot be negative');
      if (!Number.isInteger(Number(quantity))) throw new Error('Quantity must be an integer');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: parseInt(quantity)
        })
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Add item failed: ${response.status} - ${error}`);
      }

      return await response.json();
    } catch (error) {
      console.error('CartAPI.addItem error:', error);
      throw error;
    }
  }

  static async updateItem(variantId, quantity) {
    try {
      // Input validation
      if (!variantId) throw new Error('Variant ID is required');
      if (quantity < 0) throw new Error('Quantity cannot be negative');
      if (!Number.isInteger(Number(quantity))) throw new Error('Quantity must be an integer');

      // Handle line item keys vs variant IDs
      // Line item keys contain colons (e.g., "12345:abc123def456")
      // Variant IDs are just numbers (e.g., "12345")
      const id = String(variantId).includes(':') ? variantId : parseInt(variantId);

      const payload = {
        id: id,
        quantity: parseInt(quantity)
      };

      console.log('[CartAPI.updateItem] Sending payload:', payload, 'Original params:', { variantId, quantity });

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('[CartAPI.updateItem] Response error:', { status: response.status, error });
        throw new Error(`Update item failed: ${response.status} - ${error}`);
      }

      return await response.json();
    } catch (error) {
      console.error('CartAPI.updateItem error:', error);
      throw error;
    }
  }
}

class GiftManager {
  constructor() {
    this.isProcessing = false;
    this.isSyncing = false; // Prevent simultaneous sync operations
    this.debounceTime = 500; // Increased from 300ms for better debouncing
    this.retryAttempts = 3;
    this.storageKey = 'giftConfigurations';
    this.debugMode = true; // Temporarily enabled to debug threshold issue

    // Bind methods to maintain context
    this.handleQuantityChange = this.debounce(this.handleQuantityChange.bind(this), this.debounceTime);
    this.handleProductAdd = this.handleProductAdd.bind(this);
    this.handleProductRemove = this.debounce(this.handleProductRemove.bind(this), this.debounceTime);
  }

  /**
   * Initialize the gift manager
   */
  init() {
    this.log('GiftManager initializing...');
    this.bindEvents();
    this.cleanupOrphanedData();
    this.log('GiftManager initialized successfully');
  }

  /**
   * Bind event listeners for cart operations
   */
  bindEvents() {
    // Use event delegation for better performance
    document.addEventListener('click', async (e) => {
      // Handle quantity buttons - prevent event bubbling
      if (e.target.matches('.quantity__button')) {
        e.stopPropagation();
        await this.handleQuantityChange(e);
      }

      // Handle remove buttons
      if (e.target.matches('cart-remove-button') || e.target.closest('cart-remove-button')) {
        e.stopPropagation();
        await this.handleProductRemove(e);
      }
    });

    // Handle form submissions (product page add to cart)
    document.addEventListener('submit', async (e) => {
      if (e.target.matches('form[action*="/cart/add"]')) {
        await this.handleProductAdd(e);
      }
    });

    // Handle cart drawer updates
    document.addEventListener('cart:updated', () => {
      this.log('Cart updated event received');
      // Small delay to ensure cart is fully updated
      setTimeout(() => this.syncGifts(), 100);
    });
  }

  /**
   * Handle quantity change events (plus/minus buttons)
   */
  async handleQuantityChange(event) {
    if (this.isProcessing) {
      this.log('Already processing quantity change, ignoring duplicate call');
      return;
    }

    const button = event.target;
    const variantId = button.dataset.variantid;

    if (!variantId || !this.hasGiftConfiguration(variantId)) {
      return;
    }

    // Set processing flag immediately to prevent duplicate calls
    this.isProcessing = true;

    try {
      this.log('Quantity change detected for variant:', variantId, '- waiting for Shopify to finish...');

      // Let Shopify handle the quantity change first, then check if we need to sync
      await this.waitForCartUpdate();

      // Now check if the final result requires gift sync
      const finalCart = await CartAPI.getCart();
      const currentItem = finalCart.items.find(item => item.variant_id == variantId);

      if (!currentItem) {
        this.log('Item not found in final cart state:', variantId);
        return;
      }

      const finalQty = currentItem.quantity;
      this.log('Final quantity after Shopify update:', finalQty);

      // Check if current gift state matches what should be active at this quantity
      if (this.giftStateMatchesQuantity(variantId, finalQty, finalCart)) {
        this.log('Gift state already matches quantity', finalQty, '- no sync needed');
        return;
      }

      this.log('Gift state needs syncing for quantity:', finalQty);
      this.showLoading();

      try {
        // Sync gifts based on final cart state
        await this.syncGifts();

      } catch (error) {
        this.log('Error in handleQuantityChange:', error);
        this.showError('Failed to update gifts');
      } finally {
        this.hideLoading();
      }

    } finally {
      // Clear processing flag after a short delay to prevent rapid duplicate calls
      setTimeout(() => {
        this.isProcessing = false;
      }, 300);
    }
  }

  /**
   * Check if current gift state matches what should be active for this quantity
   */
  giftStateMatchesQuantity(variantId, quantity, cart) {
    const config = this.getConfiguration(variantId);
    if (!config || !config.gifts) return true; // No gifts configured

    const currentGifts = this.getCurrentGiftsInCart(cart);
    const requiredGifts = {};

    // Calculate what gifts should be active for this quantity
    config.gifts.forEach(gift => {
      if (gift.threshold <= quantity) {
        const giftVariantId = parseInt(gift.variant_id);
        requiredGifts[giftVariantId] = (requiredGifts[giftVariantId] || 0) + 1;
      }
    });

    // Check if current state matches required state
    const currentGiftIds = Object.keys(currentGifts).map(id => parseInt(id)).sort();
    const requiredGiftIds = Object.keys(requiredGifts).map(id => parseInt(id)).sort();

    const stateMatches = JSON.stringify(currentGiftIds) === JSON.stringify(requiredGiftIds) &&
      Object.keys(requiredGifts).every(giftId =>
        currentGifts[giftId] === requiredGifts[giftId]
      );

    this.log('Gift state comparison:', {
      quantity,
      currentGifts,
      requiredGifts,
      stateMatches
    });

    return stateMatches;
  }

  /**
   * Handle product addition from product page
   */
  async handleProductAdd(event) {
    if (this.isProcessing) return;

    const form = event.target;
    const variantIdInput = form.querySelector('input[name="id"]');

    if (!variantIdInput) return;

    const variantId = variantIdInput.value;

    if (!this.hasGiftConfiguration(variantId)) {
      return;
    }

    this.log('Handling product add for variant:', variantId);
    this.isProcessing = true;

    try {
      // Wait for Shopify's add to cart to complete
      await this.waitForCartUpdate(2000);

      // Sync gifts based on new cart state
      await this.syncGifts();

    } catch (error) {
      this.log('Error in handleProductAdd:', error);
      this.showError('Failed to add gifts');
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Handle product removal from cart
   */
  async handleProductRemove(event) {
    if (this.isProcessing) return;

    const button = event.target.closest('cart-remove-button') || event.target;
    const variantId = button.dataset.variantid;

    if (!variantId || !this.hasGiftConfiguration(variantId)) {
      return;
    }

    this.log('Handling product remove for variant:', variantId);
    this.isProcessing = true;
    this.showLoading();

    try {
      // Wait for Shopify's native cart update to complete
      await this.waitForCartUpdate();

      // Sync gifts (will remove orphaned gifts)
      await this.syncGifts();

    } catch (error) {
      this.log('Error in handleProductRemove:', error);
      this.showError('Failed to update gifts');
    } finally {
      this.isProcessing = false;
      this.hideLoading();
    }
  }

  /**
   * Wait for cart update to complete - with actual change detection
   */
  async waitForCartUpdate(maxWait = 3000) {
    const startTime = Date.now();
    let initialCart;

    try {
      initialCart = await CartAPI.getCart();
    } catch (error) {
      // Fallback to timeout if we can't get initial cart
      return new Promise(resolve => setTimeout(resolve, 1500));
    }

    return new Promise((resolve) => {
      const checkUpdate = async () => {
        if (Date.now() - startTime > maxWait) {
          resolve(); // Timeout fallback
          return;
        }

        try {
          const currentCart = await CartAPI.getCart();
          if (JSON.stringify(currentCart.items) !== JSON.stringify(initialCart.items)) {
            resolve(); // Cart has changed
          } else {
            setTimeout(checkUpdate, 100); // Check again in 100ms
          }
        } catch (error) {
          setTimeout(checkUpdate, 100);
        }
      };

      checkUpdate();
    });
  }

  /**
   * Main gift synchronization logic
   */
  async syncGifts() {
    // Wait for existing sync to complete instead of just returning
    if (this.isSyncing) {
      this.log('Sync already in progress, waiting for completion...');
      while (this.isSyncing) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      return;
    }

    this.isSyncing = true;

    try {
      this.log('Starting gift synchronization...');

      const cart = await CartAPI.getCart();
      const currentGifts = this.getCurrentGiftsInCart(cart);
      const requiredGifts = this.calculateRequiredGifts(cart);
      const changes = this.calculateGiftChanges(currentGifts, requiredGifts);

      this.log('Gift sync analysis:', {
        currentGifts,
        requiredGifts,
        changes
      });

      if (this.hasGiftChanges(changes)) {
        await this.executeGiftChanges(changes);
        this.refreshCartUI();
      }

      this.log('Gift synchronization completed');

    } catch (error) {
      this.log('Error in syncGifts:', error);
      throw error;
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Get current gifts in cart
   */
  getCurrentGiftsInCart(cart) {
    const configurations = this.getConfigurations();
    const giftVariantIds = new Set();

    // Collect all gift variant IDs from configurations
    Object.values(configurations).forEach(config => {
      config.gifts.forEach(gift => {
        giftVariantIds.add(parseInt(gift.variant_id));
      });
    });

    const giftsInCart = {};
    cart.items.forEach(item => {
      if (giftVariantIds.has(item.variant_id)) {
        giftsInCart[item.variant_id] = item.quantity;
      }
    });

    return giftsInCart;
  }

  /**
   * Calculate what gifts should be in cart based on current products
   */
  calculateRequiredGifts(cart) {
    const configurations = this.getConfigurations();
    const requiredGifts = {};

    cart.items.forEach(item => {
      const config = configurations[item.variant_id];

      if (config && config.gifts) {
        // Find gifts that should be active for this quantity
        const activeGifts = config.gifts.filter(gift =>
          gift.threshold <= item.quantity
        );

        activeGifts.forEach(gift => {
          const giftVariantId = parseInt(gift.variant_id);
          // Each product qualifies for one of each applicable gift
          requiredGifts[giftVariantId] = (requiredGifts[giftVariantId] || 0) + 1;
        });
      }
    });

    return requiredGifts;
  }

  /**
   * Calculate what changes need to be made to gifts
   */
  calculateGiftChanges(current, required) {
    const toAdd = {};
    const toRemove = {};

    // Check what needs to be added
    Object.keys(required).forEach(giftId => {
      const currentQty = current[giftId] || 0;
      const requiredQty = required[giftId];

      if (requiredQty > currentQty) {
        toAdd[giftId] = requiredQty - currentQty;
      }
    });

    // Check what needs to be removed
    Object.keys(current).forEach(giftId => {
      const currentQty = current[giftId];
      const requiredQty = required[giftId] || 0;

      if (currentQty > requiredQty) {
        toRemove[giftId] = currentQty - requiredQty;
      }
    });

    return { toAdd, toRemove };
  }

  /**
   * Check if there are any gift changes to make
   */
  hasGiftChanges(changes) {
    return Object.keys(changes.toAdd).length > 0 ||
      Object.keys(changes.toRemove).length > 0;
  }

  /**
   * Execute gift changes with retry logic
   */
  async executeGiftChanges(changes) {
    this.log('executeGiftChanges called with:', changes);

    // Process additions first - sequential to avoid overwhelming API
    for (const [giftId, quantity] of Object.entries(changes.toAdd)) {
      const parsedGiftId = parseInt(giftId);
      this.log('Adding gift - Original ID:', giftId, 'Parsed ID:', parsedGiftId, 'Quantity:', quantity);

      await this.retry(() => CartAPI.addItem(parsedGiftId, quantity));
    }

    // Get fresh cart state for removals
    const currentCart = await CartAPI.getCart();
    this.log('Current cart for gift removal:', currentCart.items);

    // Process removals sequentially
    for (const [giftId, removeQuantity] of Object.entries(changes.toRemove)) {
      const parsedGiftId = parseInt(giftId);

      this.log('Processing gift removal - Original ID:', giftId, 'Parsed ID:', parsedGiftId, 'Remove Qty:', removeQuantity);

      const currentItem = currentCart.items.find(item => item.variant_id === parsedGiftId);
      const currentQuantity = currentItem ? currentItem.quantity : 0;
      const newQuantity = Math.max(0, currentQuantity - removeQuantity);

      this.log('Gift removal calculation:', {
        giftId,
        parsedGiftId,
        currentItem: currentItem ? `${currentItem.variant_id} (qty: ${currentItem.quantity})` : 'not found',
        currentQuantity,
        removeQuantity,
        newQuantity
      });

      // Use line item key if available, fallback to variant ID
      const updateId = currentItem && currentItem.key ? currentItem.key : parsedGiftId;

      this.log('Using update ID:', updateId, 'Type:', typeof updateId);

      await this.retry(() => CartAPI.updateItem(updateId, newQuantity));
    }

    this.log('All gift operations completed sequentially');
  }

  /**
   * Set gift configuration for a product variant
   */
  setConfiguration(variantId, gifts) {
    const configurations = this.getConfigurations();

    configurations[variantId] = {
      gifts: gifts.map(gift => ({
        id: `gift_${gift.variant_id}`,
        variant_id: gift.variant_id,
        title: gift.title,
        threshold: gift.threshold,
        image_url: gift.image_url || '',
        price: gift.price || '0.00'
      }))
    };

    this.saveConfigurations(configurations);
    this.log('Configuration set for variant:', variantId, gifts);
  }

  /**
   * Get gift configuration for a variant
   */
  getConfiguration(variantId) {
    const configurations = this.getConfigurations();
    return configurations[variantId] || null;
  }

  /**
   * Check if variant has gift configuration
   */
  hasGiftConfiguration(variantId) {
    return this.getConfiguration(variantId) !== null;
  }

  /**
   * Get all configurations from localStorage
   */
  getConfigurations() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      this.log('Error parsing gift configurations:', error);
      return {};
    }
  }

  /**
   * Save configurations to localStorage
   */
  saveConfigurations(configurations) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(configurations));
    } catch (error) {
      this.log('Error saving gift configurations:', error);
    }
  }

  /**
   * Clean up orphaned data from old implementation
   */
  cleanupOrphanedData() {
    const oldKeys = ['giftData', 'allGiftData', 'giftCount'];
    oldKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        this.log('Cleaning up old localStorage key:', key);
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Show loading state
   */
  showLoading() {
    // Use original loading system
    const loader = document.querySelector('.loader');
    const cartDrawer = document.querySelector('#CartDrawer');

    if (loader) loader.style.display = 'block';
    if (cartDrawer) cartDrawer.classList.add('loader-active');
  }

  /**
   * Hide loading state
   */
  hideLoading() {
    const loader = document.querySelector('.loader');
    const cartDrawer = document.querySelector('#CartDrawer');

    if (loader) loader.style.display = 'none';
    if (cartDrawer) cartDrawer.classList.remove('loader-active');
  }

  /**
   * Show error message to user
   */
  showError(message) {
    console.error('Gift Manager Error:', message);
    // Could be extended to show user-facing notifications
  }

  /**
   * Refresh cart UI
   */
  refreshCartUI() {
    try {
      // Method 1: Try Shopify's cart refresh if available
      if (window.Shopify && window.Shopify.theme && window.Shopify.theme.cart) {
        window.Shopify.theme.cart.refresh();
        return;
      }

      // Method 2: Dispatch cart updated event for Shopify themes
      document.dispatchEvent(new CustomEvent('cart:updated'));

      // Method 3: Force refresh cart drawer using jQuery if available
      CartAPI.getCart()
        .then(cart => {
          // Update cart count in header/navbar
          const countElements = document.querySelectorAll('.cart-count-bubble, [data-cart-count], .cart-count');
          countElements.forEach(el => {
            if (el) {
              el.textContent = cart.item_count;
              if (cart.item_count > 0) {
                el.style.display = 'block';
              }
            }
          });

          // Try to refresh cart drawer content
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            // Method 3a: Use jQuery load if available
            if (window.$ && window.$.fn.load) {
              window.$('cart-drawer').load(window.location.href + ' #CartDrawer');
            }
            // Method 3b: Manual refresh
            else {
              this.refreshCartDrawerManually();
            }
          }

          this.log('Cart UI refresh completed - count updated to:', cart.item_count);
        })
        .catch(error => this.log('Error updating cart count:', error));

    } catch (error) {
      this.log('Error in refreshCartUI:', error);
      // Fallback - try manual refresh
      this.refreshCartDrawerManually();
    }
  }

  /**
   * Manual cart drawer refresh as fallback
   */
  refreshCartDrawerManually() {
    try {
      fetch(window.location.href)
        .then(response => response.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newDrawer = doc.querySelector('#CartDrawer');
          const currentDrawer = document.querySelector('#CartDrawer');

          if (newDrawer && currentDrawer) {
            // Replace the cart drawer content
            currentDrawer.innerHTML = newDrawer.innerHTML;
            this.log('Manual cart drawer refresh completed');
          }
        })
        .catch(error => this.log('Error in manual cart refresh:', error));
    } catch (error) {
      this.log('Error in refreshCartDrawerManually:', error);
    }
  }

  /**
   * Utility: Debounce function calls
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Utility: Retry function with exponential backoff
   */
  async retry(func, attempts = this.retryAttempts) {
    for (let i = 0; i < attempts; i++) {
      try {
        return await func();
      } catch (error) {
        if (i === attempts - 1) throw error;

        const delay = Math.pow(2, i) * 1000; // Exponential backoff
        this.log(`Retry attempt ${i + 1} failed, retrying in ${delay}ms:`, error);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Utility: Logging with debug mode
   */
  log(message, data = null) {
    if (this.debugMode) {
      console.log(`[GiftManager] ${message}`, data);
    }
  }
}

// Expose classes to global scope for debugging and external access
window.GiftManager = GiftManager;
window.CartAPI = CartAPI;

// Initialize the gift manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.giftManager = new GiftManager();
    window.giftManager.init();
  });
} else {
  window.giftManager = new GiftManager();
  window.giftManager.init();
} 
